import axios from 'axios';
import { useState, useEffect, useMemo } from 'react';
import { Clock, AlertTriangle, Edit3, Calendar, Lock, CheckCircle2, ShieldCheck } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '@/lib/AuthContext';
import { API_URL, getHeuresParJour } from './utils';

/**
 * Bandeau Heures Budgétées d'un bon de travail.
 *
 * 🆕 [3A.4] Intègre le workflow d'approbation des heures budgétées :
 *   • proprietaire : voit Proposition + Officielles + boutons Approuver / Révoquer
 *   • charge_projet / adjoint_admin : voit Proposition + bandeau "en attente" / Officielles
 *   • chef_equipe / peintre / sous_traitant / installateur :
 *       - approuvé → voit Officielles (règle 48h préservée)
 *       - non approuvé → 🔒 cadenas + "en attente d'approbation propriétaire"
 *                        AUCUN chiffre exposé.
 *
 * Source de vérité : bon.heures_budgetees_officielles (None = pas approuvé).
 */
export function HeuresBannerBon({ bon, onApproved }) {
  const { isProprietaire, isAdmin, user } = useAuth();
  const role = user?.role || '';
  const isWorker = ['chef_equipe', 'peintre', 'sous_traitant', 'installateur'].includes(role); // Cat H — dispatch UI selon rôle terrain (worker vs admin), pas gate RBAC.
  const isCp = role === 'charge_projet' || role === 'adjoint_admin'; // Cat H — dispatch UI : bandeau différent pour CP/adjoint (peuvent approuver heures) vs Owner/terrain. RBAC gate = `bt_approuver_heures_budgetees` (déjà existant côté matrice).  // E3b-whitelist : rôle passé en prop (dispatch UI)

  const officielles = bon.heures_budgetees_officielles;
  const isApproved = officielles !== null && officielles !== undefined;
  // 🆕 [Phase 29 — Régression Heures Budgétées 2026-06-01]
  // Fallback legacy : si `proposees` n'est pas explicitement défini mais que
  // le BT a une valeur `heures_budgetees` historique, on la traite comme
  // proposition implicite. Ça restaure l'affichage de la carte "À approuver"
  // pour les BT pré-Phase 13 (et tous ceux qui n'ont jamais eu de proposition
  // explicite). Sans ce fallback, l'UI restait vide bien que le composant
  // soit monté.
  const proposeesRaw = bon.heures_budgetees_proposees;
  const heuresBudgLegacy = bon.heures_budgetees;
  const proposees = (proposeesRaw !== null && proposeesRaw !== undefined)
    ? proposeesRaw
    : (heuresBudgLegacy !== null && heuresBudgLegacy !== undefined && Number(heuresBudgLegacy) > 0
        ? Number(heuresBudgLegacy)
        : null);
  const approbation = bon.heures_budgetees_approbation || null;
  const masquees = bon.heures_masquees_pour_role === true;

  const [punches, setPunches] = useState([]);
  const [aLHeure, setALHeure] = useState(bon.a_l_heure || false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [approveValue, setApproveValue] = useState('');
  const [saving, setSaving] = useState(false);

  // Heures effectives (officielles si approuvé) — utilisé pour calculer restantes
  const heuresEffectives = isApproved ? Number(officielles) : 0;

  useEffect(() => {
    setALHeure(bon.a_l_heure || false);
  }, [bon.a_l_heure]);

  useEffect(() => {
    axios.get(`${API_URL}/api/punch/bon-travail/${bon.id}`)
      .then(r => r.status === 200 ? r.data : { punches: [] })
      .then(d => setPunches(d.punches || []))
      .catch(() => {});
  }, [bon.id]);

  // Heures validées (déduites du budget)
  // 🚚 [Phase v13.52 — 2026-07-20] Le déplacement compte dans les heures BT (Q3a).
  //     Additionne temps_deplacement_minutes/60 aux heures_nettes[_valide].
  const _heuresPunch = (p) => {
    const base = p.heures_nettes_valide ?? p.heures_nettes ?? 0;
    const depl = (parseInt(p.temps_deplacement_minutes, 10) || 0) / 60;
    return Math.max(0, base + depl);
  };
  const heuresValidees = punches
    .filter(p => p.statut === 'valide')
    .reduce((sum, p) => sum + _heuresPunch(p), 0);
  const heuresReellesPourAffichage = punches
    .filter(p => p.statut !== 'en_cours' && p.statut !== 'refuse' && p.valide !== false)
    .reduce((sum, p) => sum + _heuresPunch(p), 0);
  const heuresRestantes = heuresEffectives > 0 ? heuresEffectives - heuresValidees : null;
  const heuresEnAttenteValidation = heuresReellesPourAffichage - heuresValidees;

  // Règle "48h" préservée pour les travailleurs (les chiffres visibles
  // uniquement à moins de 48h du début pour ne pas dévoiler trop tôt).
  const respecteRegle48h = useMemo(() => {
    if (isProprietaire || isAdmin) return true;
    if (!bon.dates_chantier || bon.dates_chantier.length === 0) return false;
    const premiereDate = bon.dates_chantier[0];
    if (!premiereDate) return false;
    const diffHeures = (new Date(premiereDate) - new Date()) / (1000 * 60 * 60);
    return diffHeures <= 48;
  }, [bon.dates_chantier, isProprietaire, isAdmin]);

  // ── Helpers approbation ─────────────────────────────────────────
  const ouvrirDialogApprobation = () => {
    const defaultVal = isApproved
      ? String(officielles ?? '')
      : (proposees !== null && proposees !== undefined ? String(proposees) : '');
    setApproveValue(defaultVal);
    setShowApproveDialog(true);
  };

  const handleApprouver = async () => {
    const val = parseFloat(approveValue);
    if (Number.isNaN(val) || val < 0 || val > 10000) {
      toast.error('Heures invalides (0 à 10000)');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/crm/bons-travail/${bon.id}/heures/approuver`, { heures: val });
      toast.success(`Heures approuvées : ${val.toFixed(1).replace('.', ',')} h. Visible pour toute l'équipe.`);
      setShowApproveDialog(false);
      if (onApproved) onApproved();
      // Petite hack : recharger la page si pas de callback (BonTravailDetail recharge déjà via SWR)
      else window.location.reload();
    } catch (e) {
      toast.error('Erreur lors de l\'approbation');
    } finally {
      setSaving(false);
    }
  };

  const handleRevoquer = async () => {
    if (!window.confirm("Révoquer l'approbation des heures ? Les travailleurs ne verront plus le chiffre tant qu'il n'est pas réapprouvé.")) return;
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/crm/bons-travail/${bon.id}/heures/revoquer`);
      toast.success('Approbation révoquée');
      if (onApproved) onApproved();
      else window.location.reload();
    } catch (e) {
      toast.error('Erreur lors de la révocation');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleALHeure = async (checked) => {
    if (!isProprietaire) return;
    try {
      await axios.patch(`${API_URL}/api/crm/bons-travail/${bon.id}/a-l-heure`, { a_l_heure: checked });
      setALHeure(checked);
      toast.success(checked ? 'Bon marqué "À l\'heure"' : 'Heures budgétées réactivées (en attente d\'approbation)');
      if (onApproved) onApproved();
      else window.location.reload();
    } catch {
      toast.error('Erreur');
    }
  };

  // ── États visuels ────────────────────────────────────────────────
  const delta = useMemo(() => {
    if (!isApproved || proposees === null || proposees === undefined) return null;
    return Number(officielles) - Number(proposees);
  }, [officielles, proposees, isApproved]);

  // 1. Worker + non-approuvé (ou masqué règle 48h) → cadenas
  if (isWorker && !aLHeure && (!isApproved || masquees)) {
    return (
      <div
        className="flex flex-wrap gap-3 items-center p-3 bg-amber-50 border border-amber-200 rounded-xl no-print mb-2"
        data-testid="heures-banner-locked-worker"
      >
        <div className="flex items-center gap-2 bg-white border border-amber-300 rounded-lg px-3 py-2">
          <Lock className="w-4 h-4 text-amber-600 shrink-0" />
          <div>
            <p className="text-xs text-amber-700 font-semibold">Heures budgétées</p>
            <p className="text-sm text-amber-800">En attente d'approbation du propriétaire</p>
          </div>
        </div>
        {/* Heures réelles tout de même visibles */}
        {heuresReellesPourAffichage > 0 && (
          <div className="flex items-center gap-2 bg-white border rounded-lg px-3 py-2">
            <Clock className="w-4 h-4 text-blue-500 shrink-0" />
            <div>
              <p className="text-xs text-gray-500">Heures réelles</p>
              <p className="text-sm font-bold text-blue-700">{heuresReellesPourAffichage.toFixed(2).replace('.', ',')}h</p>
              {heuresEnAttenteValidation > 0 && (
                <p className="text-xs text-yellow-600">{heuresEnAttenteValidation.toFixed(2).replace('.', ',')}h en attente validation</p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Détermine s'il faut afficher la carte heures budgétées
  // 🆕 [Phase 29 — Double approbation 2026-06-01]
  // La case "Heures budgétées" est TOUJOURS visible (inline avec À l'heure / Heures réelles).
  // Le contenu varie selon le rôle :
  //   - non-owner + non-approuvé → "En attente d'approbation" (chiffre masqué)
  //   - non-owner + approuvé → valeur officielle
  //   - owner + non-approuvé → proposees + bouton Approuver (désactivé si user_id === proposees_par)
  //   - owner + approuvé → valeur officielle + badge "Approuvé par X"
  const hasProposition = proposees !== null && proposees !== undefined && Number(proposees) > 0;
  const showBudgeteesCard = !aLHeure && (hasProposition || isApproved);
  // Empêcher l'owner d'auto-approuver sa propre proposition (double validation)
  const proposeesParUserId = bon.heures_budgetees_proposees_par_user_id;
  const currentUserId = user?.id;
  const isOwnProposition = !!(proposeesParUserId && currentUserId && proposeesParUserId === currentUserId);
  // (anciens flags conservés pour les blocs d'historique/restantes plus bas)
  const showProposition = !aLHeure && (isProprietaire || isCp) && !isApproved && hasProposition;
  const showOfficielle = !aLHeure && isApproved && (isProprietaire || isAdmin || (isWorker && respecteRegle48h) || isCp);
  const hideEverything = !isProprietaire && !aLHeure && heuresEffectives === 0 && heuresReellesPourAffichage === 0 && !showProposition;

  if (hideEverything) return null;

  return (
    <>
      <div className="flex flex-wrap gap-3 items-center p-3 bg-slate-50 border border-slate-200 rounded-xl no-print mb-2">
        {/* À l'heure (propriétaire) */}
        {isProprietaire && (
          <div className="flex items-center gap-2 bg-white border rounded-lg px-3 py-2">
            <Checkbox
              id={`a-l-heure-${bon.id}`}
              checked={aLHeure}
              onCheckedChange={handleToggleALHeure}
              data-testid="checkbox-a-l-heure"
            />
            <label htmlFor={`a-l-heure-${bon.id}`} className="text-sm font-medium text-slate-700 cursor-pointer select-none">
              À l'heure
            </label>
          </div>
        )}
        {aLHeure && !isProprietaire && (
          <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2" data-testid="badge-a-l-heure">
            <Clock className="w-4 h-4 text-blue-500" />
            <span className="text-sm font-semibold text-blue-700">À l'heure</span>
          </div>
        )}

        {/* 🆕 [Phase 29 — Double approbation 2026-06-01]
            UNE SEULE case "Heures budgétées" inline (à côté de À l'heure + Heures réelles).
            Contenu adapté au rôle + état d'approbation.
            - Non-owner + non-approuvé → "En attente d'approbation" (chiffre masqué)
            - Owner + non-approuvé → valeur proposée + bouton Approuver (désactivé si auto-prop)
            - Tout-monde + approuvé → valeur officielle + badge "Approuvé par X" (owner uniquement)
        */}
        {showBudgeteesCard && (
          <div
            className={
              isApproved
                ? "flex items-center gap-2 bg-white border rounded-lg px-3 py-2"
                : "flex items-center gap-2 bg-amber-50 border-2 border-amber-400 rounded-lg px-3 py-2 shadow-sm"
            }
            data-testid="heures-budgetees-card"
          >
            <Clock className={isApproved ? "w-4 h-4 text-slate-500 shrink-0" : "w-4 h-4 text-amber-600 shrink-0"} />
            <div>
              <div className="flex items-center gap-1">
                <p className="text-xs text-gray-500">Heures budgétées</p>
                {isApproved && (isProprietaire || isAdmin || isCp) && approbation?.approved_by_nom && (() => {
                  // 🆕 [Phase 29 — Historique tooltip] Construire le tooltip chronologique
                  const histo = Array.isArray(bon.heures_budgetees_historique_approbations)
                    ? bon.heures_budgetees_historique_approbations
                    : [];
                  const formatLine = (e) => {
                    const t = e.timestamp || e.approved_at || '';
                    const dt = t ? new Date(t) : null;
                    const stamp = dt && !isNaN(dt) ? dt.toLocaleString('fr-CA', { dateStyle: 'short', timeStyle: 'short' }) : '';
                    const labels = {
                      'proposition': '📝 Proposé par',
                      'modification_post_approbation': '✏️ Modifié par',
                      'approve': '✅ Approuvé par',
                    };
                    const lbl = labels[e.action] || e.action || 'Action';
                    const nom = e.user_nom || e.approved_by_nom || '?';
                    const val = e.valeur != null ? ` (${Number(e.valeur).toFixed(1).replace('.', ',')}h)` : '';
                    return `• ${lbl} ${nom}${val} — ${stamp}`;
                  };
                  const tooltipText = histo.length > 1
                    ? `Historique d'approbation :\n${histo.map(formatLine).join('\n')}`
                    : `Approuvé par ${approbation.approved_by_nom} le ${(approbation.approved_at || '').slice(0, 10)}`;
                  return (
                    <span
                      className="text-[10px] inline-flex items-center gap-0.5 text-emerald-700 cursor-help"
                      title={tooltipText}
                      data-testid="badge-approuve"
                      data-history-count={histo.length}
                    >
                      <ShieldCheck className="w-3 h-3" /> Approuvé
                      {histo.length > 1 && (
                        <span className="ml-0.5 text-[9px] opacity-70" data-testid="badge-history-count">
                          ({histo.length})
                        </span>
                      )}
                    </span>
                  );
                })()}
                {!isApproved && (
                  <span className="bg-amber-200 text-amber-900 text-[10px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wide">
                    À approuver
                  </span>
                )}
              </div>

              {/* Affichage de la valeur — varie selon état + rôle */}
              {isApproved ? (
                <p className="text-sm font-bold text-slate-800" data-testid="heures-budgetees-value">
                  {heuresEffectives > 0
                    ? `${heuresEffectives.toFixed(1).replace('.', ',')}h`
                    : <span className="text-gray-400">0h</span>}
                </p>
              ) : (isProprietaire || isAdmin) ? (
                <p className="text-base font-bold text-amber-900" data-testid="heures-budgetees-value">
                  {Number(proposees).toFixed(1).replace('.', ',')} h
                </p>
              ) : (
                <p className="text-xs italic text-amber-800" data-testid="heures-budgetees-pending">
                  ⏳ En attente d'approbation
                </p>
              )}

              {/* Action owner : Approuver (si non-approuvé + proposees existant) */}
              {/* 🔧 [Phase 39] Plus de blocage auto-approbation : tous les
                  propriétaires peuvent approuver, y compris le proposant.
                  L'historique conserve la traçabilité honnête. */}
              {!isApproved && isProprietaire && hasProposition && (
                <Button
                  size="sm"
                  className="mt-1 h-7 bg-emerald-600 hover:bg-emerald-700 font-semibold"
                  onClick={ouvrirDialogApprobation}
                  title={isOwnProposition
                    ? "Approuver les heures budgétées (vous êtes aussi le proposant — l'historique en gardera trace)"
                    : "Approuver les heures budgétées"}
                  data-testid="btn-approuver-heures"
                >
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                  Approuver
                </Button>
              )}

              {/* Owner actions sur valeur approuvée : Modifier / Révoquer */}
              {isApproved && isProprietaire && (
                <div className="flex items-center gap-2 mt-1">
                  <button
                    onClick={ouvrirDialogApprobation}
                    className="text-[11px] inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded text-blue-700 font-medium transition-colors"
                    data-testid="btn-modifier-approbation"
                  >
                    <Edit3 className="w-3 h-3" /> Modifier
                  </button>
                  <button
                    onClick={handleRevoquer}
                    disabled={saving}
                    className="text-[10px] text-slate-400 hover:text-red-600 underline"
                    data-testid="btn-revoquer-approbation"
                  >
                    Révoquer
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Bloc legacy showProposition retiré : remplacé par showBudgeteesCard ci-dessus */}
        {false && showProposition && (
          <div
            className="flex items-center gap-2 bg-amber-50 border-2 border-amber-400 rounded-lg px-3 py-2 shadow-sm"
            data-testid="heures-proposition-card"
          >
            <Clock className="w-4 h-4 text-amber-600 shrink-0" />
            <div>
              <p className="text-xs text-amber-700 font-semibold flex items-center gap-1">
                <span className="bg-amber-200 text-amber-900 text-[10px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wide">À approuver</span>
                Proposition
              </p>
              <p className="text-base font-bold text-amber-900" data-testid="heures-proposition-value">
                {Number(proposees).toFixed(1).replace('.', ',')} h
              </p>
              {isCp && !isProprietaire && (
                <p className="text-[10px] text-amber-700 mt-0.5">⏳ En attente d'approbation propriétaire</p>
              )}
            </div>
            {isProprietaire && (
              <Button
                size="sm"
                className="ml-2 h-8 bg-emerald-600 hover:bg-emerald-700 font-semibold"
                onClick={ouvrirDialogApprobation}
                data-testid="btn-approuver-heures"
              >
                <CheckCircle2 className="w-4 h-4 mr-1" />
                Approuver
              </Button>
            )}
          </div>
        )}

        {/* OFFICIELLE legacy retiré : remplacé par showBudgeteesCard ci-dessus */}
        {false && showOfficielle && (
          <div
            className="flex items-center gap-2 bg-white border rounded-lg px-3 py-2"
            data-testid="heures-officielle-card"
          >
            <Clock className="w-4 h-4 text-slate-500 shrink-0" />
            <div>
              <div className="flex items-center gap-1">
                <p className="text-xs text-gray-500">Heures budgétées</p>
                {(isProprietaire || isAdmin || isCp) && approbation?.approved_by_nom && (
                  <span
                    className="text-[10px] inline-flex items-center gap-0.5 text-emerald-700"
                    title={`Approuvé par ${approbation.approved_by_nom} le ${(approbation.approved_at || '').slice(0, 10)}`}
                    data-testid="badge-approuve"
                  >
                    <ShieldCheck className="w-3 h-3" />
                    Approuvé
                  </span>
                )}
              </div>
              <p className="text-sm font-bold text-slate-800 flex items-center gap-1" data-testid="heures-officielle-value">
                {heuresEffectives > 0
                  ? `${heuresEffectives.toFixed(1).replace('.', ',')}h`
                  : <span className="text-gray-400">0h (à l'heure)</span>}
              </p>
              {isProprietaire && (
                <div className="flex items-center gap-2 mt-1">
                  <button
                    onClick={ouvrirDialogApprobation}
                    className="text-[11px] inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded text-blue-700 font-medium transition-colors"
                    title="Modifier la valeur officielle approuvée"
                    data-testid="btn-modifier-approbation"
                  >
                    <Edit3 className="w-3 h-3" />
                    Modifier
                  </button>
                  <button
                    onClick={handleRevoquer}
                    disabled={saving}
                    className="text-[10px] text-slate-400 hover:text-red-600 underline"
                    data-testid="btn-revoquer-approbation"
                  >
                    Révoquer
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Heures réelles — toujours visible */}
        <div className="flex items-center gap-2 bg-white border rounded-lg px-3 py-2" data-testid="heures-reelles-card">
          <Clock className="w-4 h-4 text-blue-500 shrink-0" />
          <div>
            <p className="text-xs text-gray-500">Heures réelles</p>
            <p className="text-sm font-bold text-blue-700">{heuresReellesPourAffichage.toFixed(2).replace('.', ',')}h</p>
            {heuresEnAttenteValidation > 0 && (
              <p className="text-xs text-yellow-600">{heuresEnAttenteValidation.toFixed(2).replace('.', ',')}h en attente validation</p>
            )}
          </div>
        </div>

        {/* Heures restantes — visible si approuvé > 0 */}
        {heuresEffectives > 0 && showOfficielle && (
          <div
            className={`flex items-center gap-2 border rounded-lg px-3 py-2 ${heuresRestantes < 0 ? 'bg-red-50 border-red-200' : heuresRestantes < heuresEffectives * 0.15 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}
            data-testid="heures-restantes-card"
          >
            <Clock className={`w-4 h-4 shrink-0 ${heuresRestantes < 0 ? 'text-red-500' : heuresRestantes < heuresEffectives * 0.15 ? 'text-amber-500' : 'text-green-500'}`} />
            <div>
              <p className="text-xs text-gray-500">Heures restantes</p>
              <p className={`text-sm font-bold ${heuresRestantes < 0 ? 'text-red-700' : heuresRestantes < heuresEffectives * 0.15 ? 'text-amber-700' : 'text-green-700'}`}>
                {heuresRestantes < 0
                  ? `${Math.abs(heuresRestantes).toFixed(2).replace('.', ',')}h dépassement`
                  : `${heuresRestantes.toFixed(2).replace('.', ',')}h`}
              </p>
              <div className="w-24 bg-gray-200 rounded-full h-1.5 mt-1">
                <div
                  className={`h-1.5 rounded-full transition-all ${heuresRestantes < 0 ? 'bg-red-500' : 'bg-green-500'}`}
                  style={{ width: `${Math.min(100, (heuresValidees / heuresEffectives) * 100)}%` }}
                />
              </div>
              {heuresEnAttenteValidation > 0 && (
                <p className="text-[10px] text-gray-500 mt-0.5">
                  ⚠️ {heuresEnAttenteValidation.toFixed(1).replace('.', ',')}h non validées (non déduites)
                </p>
              )}
            </div>
          </div>
        )}

        {/* Estimation du nombre de jours (basée sur officielle approuvée) */}
        {heuresEffectives > 0 && !aLHeure && showOfficielle && (() => {
          const hpj = getHeuresParJour(bon.dates_chantier);
          const assignes = bon.employes_assignes || [];
          const nbMembres = assignes.length > 0 ? assignes.length : 1;
          const joursEstimes = nbMembres > 0
            ? Math.ceil((heuresEffectives / (hpj * nbMembres)) * 10) / 10
            : Math.ceil((heuresEffectives / hpj) * 10) / 10;
          const saison = hpj === 8 ? 'été' : 'hiver';
          const nbJoursPlanifies = (bon.dates_chantier || []).length;
          const diff = nbJoursPlanifies > 0 ? nbJoursPlanifies - joursEstimes : 0;
          const showCounter = nbJoursPlanifies > 0;
          const showAlert = nbJoursPlanifies > 0 && Math.abs(diff) >= 0.5;
          const manque = diff < 0;
          return (
            <>
              <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2" data-testid="estimation-jours-banner">
                <Calendar className="w-4 h-4 text-blue-500 shrink-0" />
                <div>
                  <p className="text-xs text-blue-500">Estimation</p>
                  <p className="text-sm font-bold text-blue-700">{joursEstimes} jour(s)</p>
                  <p className="text-[10px] text-blue-400">{heuresEffectives}h / {hpj}h×{nbMembres} ({saison})</p>
                  {showCounter && (
                    <p className="text-[10px] text-blue-600 font-medium mt-0.5">
                      {nbJoursPlanifies} planifié{nbJoursPlanifies > 1 ? 's' : ''} / {joursEstimes} estimé{joursEstimes > 1 ? 's' : ''}
                    </p>
                  )}
                </div>
              </div>
              {showAlert && (
                <div className={`flex items-center gap-2 border rounded-lg px-3 py-2 ${manque ? 'bg-amber-50 border-amber-300' : 'bg-blue-50 border-blue-200'}`} data-testid="alert-jours-mismatch-banner">
                  <AlertTriangle className={`w-4 h-4 shrink-0 ${manque ? 'text-amber-500' : 'text-blue-500'}`} />
                  <div>
                    <p className={`text-xs font-semibold ${manque ? 'text-amber-800' : 'text-blue-800'}`}>
                      {nbJoursPlanifies} planifié(s) / {joursEstimes} estimé(s)
                    </p>
                    <p className={`text-[10px] ${manque ? 'text-amber-600' : 'text-blue-500'}`}>
                      {manque ? `Il manque ~${Math.abs(diff).toFixed(1)} jour(s)` : `Marge de ~${diff.toFixed(1)} jour(s)`}
                    </p>
                  </div>
                </div>
              )}
            </>
          );
        })()}
      </div>

      {/* Modal d'approbation */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent data-testid="dialog-approuver-heures">
          <DialogHeader>
            <DialogTitle>{isApproved ? 'Modifier l\'approbation' : 'Approuver les heures budgétées'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {proposees !== null && proposees !== undefined && (
              <div className="text-sm text-slate-600 bg-slate-50 border rounded p-2">
                Proposition : <strong>{Number(proposees).toFixed(1).replace('.', ',')} h</strong>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1" htmlFor="approve-input">
                Heures à approuver
              </label>
              <input
                id="approve-input"
                type="number"
                step="0.5"
                min="0"
                max="10000"
                value={approveValue}
                onChange={e => setApproveValue(e.target.value)}
                autoFocus
                className="w-full border rounded px-2 py-1.5 text-sm font-semibold"
                data-testid="input-approve-heures"
              />
              {(() => {
                const v = parseFloat(approveValue);
                if (Number.isNaN(v) || proposees === null || proposees === undefined) return null;
                const d = v - Number(proposees);
                if (Math.abs(d) < 0.01) return null;
                return (
                  <p className={`text-[11px] mt-1 ${d > 0 ? 'text-amber-700' : 'text-blue-700'}`}>
                    Ajustement manuel : {d > 0 ? '+' : ''}{d.toFixed(1).replace('.', ',')} h vs proposition
                  </p>
                );
              })()}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveDialog(false)} disabled={saving} data-testid="btn-cancel-approve">
              Annuler
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={handleApprouver}
              disabled={saving}
              data-testid="btn-confirm-approve"
            >
              {saving ? 'Approbation...' : (isApproved ? 'Mettre à jour' : 'Approuver')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
